///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

//-----------------------------------------------------------------------------------------
//* \brief
//? Single source of truth for the official INTRASTAT rules (INS - Institutul Național
//? de Statistică). Every rule below is taken from:
//?
//?  [1] "Handbook/Implementation guideline for XML INTRASTAT declaration file"
//?      http://www.intrastat.ro/doc/Handbook_XML_2025_EN.pdf  (the XSD itself)
//?  [2] "Manual pentru furnizorii de informații statistice - Partea I"
//?      http://www.intrastat.ro/doc/Manual_pentru_furnizorii_de_informatii_statistice_partea_I_2023_v1.pdf
//?  [3] "Ghidul utilizatorului pentru Aplicaţia INTRASTAT offline"
//?      http://www.intrastat.ro/doc/Ghid_offline_2025_v1.pdf
//?
//? Relevant XSD restrictions from [1]:
//?  - Cn8CodificationType    : xsd:token, pattern [0-9]{8}   -> exactly 8 digits, no spaces
//?  - PositiveLongType       : xsd:long, minExclusive 0      -> InvoiceValue / StatisticalValue /
//?                                                              NetMass / QtyInSupplUnits must be > 0
//?  - CountryType            : xsd:token, length 1..2
//?  - VatNumberType          : xsd:token, pattern [0-9]{10}  -> declarant / DTP VAT number
//?  - NatureOfTransactionACode is mandatory, ...BCode is optional
//?
//? Every column normalizer returns { value, changed, note } and every validator returns
//? null (valid) or { severity, message }. ExcelResultTable, XMLService and
//? XmlValidationService all consume this module so a rule can never drift between them.
//-----------------------------------------------------------------------------------------

import { CoduriVamale, RefCodes } from '../databases/CodVamal'
import { countryCodes, EuCountries } from '../databases/CountryCodes'
import { TransactionNatures } from '../databases/TransactionNature'
import { TransportModes } from '../databases/TransportMode'
import { DeliveryConditions } from '../databases/DeliveryCondition'

//-----------------------------------------------------------------------------------------
//* \brief
//? Pre-built lookup structures (static data, created once at module load)
//-----------------------------------------------------------------------------------------
const CN8_LIST              = Array.from(CoduriVamale)
const CN8_INDEX             = new Map(CN8_LIST.map((code, idx) => [code, idx]))
//? 6 digit HS subheading -> how many CN8 codes hang under it. Used to expand a 6 digit
//? code only when the expansion is unambiguous (checked against the 2026 nomenclature:
//? every prefix with a single child has that child equal to prefix + "00").
const CN8_PREFIX_COUNT      = CN8_LIST.reduce((acc, code) => {
    const prefix = code.slice(0, 6)
    acc.set(prefix, (acc.get(prefix) || 0) + 1)
    return acc
}, new Map())
const WORLD_COUNTRY_SET     = new Set(countryCodes)
const EU_COUNTRY_SET        = new Set(EuCountries.Item.map(c => c.Code))
const TRANSACTION_NATURES   = new Set(TransactionNatures.map(v => `${v.categ}.${v.code}`))
const TRANSPORT_MODES       = new Set(TransportModes.map(v => v.code.toString()))
const DELIVERY_TERMS        = new Set(DeliveryConditions.map(v => v.code))

//-----------------------------------------------------------------------------------------
//* \brief
//? Reporting country. It can never be the partner country (country of destination for
//? dispatches / country of consignment for arrivals) - see [2] ch. 6.3.2.
//-----------------------------------------------------------------------------------------
export const REPORTING_COUNTRY = 'RO'

//-----------------------------------------------------------------------------------------
//* \brief
//? Placeholder partner VAT allowed by [2] ch. 6.3.1 when the partner cannot be identified
//? (sales to private persons, embassies, triangular trade with unknown final consignee).
//-----------------------------------------------------------------------------------------
export const UNKNOWN_PARTNER_VAT = 'QV999999999999'

//-----------------------------------------------------------------------------------------
//* \brief
//? Territories excluded from the INTRASTAT statistical system - the goods exchanged with
//? them are covered by customs declarations instead ([2] ch. 6.3.2 "Excluderi").
//-----------------------------------------------------------------------------------------
export const EXCLUDED_TERRITORIES = new Set([
    'FO', // Insulele Feroe
    'GL', // Groenlanda
    'XC', // Ceuta
    'XL', // Melilla
    'NC', // Noua Caledonie
    'PM', // Saint Pierre și Miquelon
    'WF', // Insulele Wallis și Futuna
    'PF', // Polinezia Franceză
    'TF', // Teritoriile australe și antarctice franceze
    'BL', // Saint-Barthelemy
    'SM', // San Marino
])

//-----------------------------------------------------------------------------------------
//* \brief
//? Severity of a finding. ERROR means the official application will reject the file, so
//? generation is blocked. WARNING means the file is accepted but the data is suspect.
//-----------------------------------------------------------------------------------------
export const SEVERITY = {
    ERROR:   'error',
    WARNING: 'warning',
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Layout field names. These are the keys used by the layout (macheta) definitions and by
//? columnMap in ExcelResultTable.
//-----------------------------------------------------------------------------------------
export const FIELD = {
    COD_VAMAL:          'codVamal',
    VALOARE_FISCALA:    'valoareFiscala',
    GREUTATE_NETA:      'greutateNeta',
    CONDITIE_LIVRARE:   'conditieLivrare',
    TARA_ORIGINE:       'taraOrigine',
    TARA_EXPEDIERE:     'taraExpediere',
    VALOARE_STATISTICA: 'valoareStatistica',
    NATURA_TRANZACTIEI: 'naturaTranzactiei',
    MOD_TRANSPORT:      'modTransport',
    COD_UMS:            'codUms',
    VALOARE_UMS:        'valoareUms',
    VAT:                'vat',
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Position of each field inside the flat item array produced by getArrayWithRequired().
//? Kept in sync with REQUIRED_HEADERS order.
//-----------------------------------------------------------------------------------------
export const ITEM = {
    CN8:                0,
    INVOICE_VALUE:      1,
    NET_MASS:           2,
    DELIVERY_TERMS:     3,
    COUNTRY_ORIGIN:     4,
    COUNTRY_PARTNER:    5,
    STAT_VALUE:         6,
    TRANSACTION_NATURE: 7,
    TRANSPORT_MODE:     8,
    SUPPL_UNIT_CODE:    9,
    SUPPL_UNIT_QTY:    10,
    PARTNER_VAT:       11,
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Human readable Romanian labels used in every error message shown to the user.
//-----------------------------------------------------------------------------------------
export const FIELD_LABELS = {
    [FIELD.COD_VAMAL]:          'Cod NC8',
    [FIELD.VALOARE_FISCALA]:    'Valoare fiscală',
    [FIELD.GREUTATE_NETA]:      'Greutate netă',
    [FIELD.CONDITIE_LIVRARE]:   'Condiție de livrare',
    [FIELD.TARA_ORIGINE]:       'Țara de origine',
    [FIELD.TARA_EXPEDIERE]:     'Țara de expediere/destinație',
    [FIELD.VALOARE_STATISTICA]: 'Valoare statistică',
    [FIELD.NATURA_TRANZACTIEI]: 'Natura tranzacției',
    [FIELD.MOD_TRANSPORT]:      'Mod de transport',
    [FIELD.COD_UMS]:            'Cod UM suplimentară',
    [FIELD.VALOARE_UMS]:        'Cantitate UM suplimentară',
    [FIELD.VAT]:                'Cod TVA partener',
}

//=========================================================================================
//? HELPERS
//=========================================================================================

//-----------------------------------------------------------------------------------------
//* \brief
//? Safe string conversion - null/undefined become an empty string instead of "null".
//-----------------------------------------------------------------------------------------
export const asString = (value) => {
    if (value === null || value === undefined) return ''
    return value.toString()
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Parses a number written in any of the formats an Excel export can produce:
//? "1234.56", "1234,56", "1.234,56", "1,234.56", "1 234,56". Returns NaN if not a number.
//-----------------------------------------------------------------------------------------
export const parseLooseNumber = (raw) => {
    const text = asString(raw).replace(/\s/g, '').replace(/[^0-9.,+-]/g, '')
    if (text === '') return NaN

    const lastDot   = text.lastIndexOf('.')
    const lastComma = text.lastIndexOf(',')

    let normalized = text
    if (lastDot !== -1 && lastComma !== -1) {
        // The separator that appears last is the decimal one, the other groups thousands
        normalized = lastComma > lastDot
            ? text.replace(/\./g, '').replace(',', '.')
            : text.replace(/,/g, '')
    }
    else if (lastComma !== -1) {
        // A single comma is a decimal separator ("1234,56"), unless it groups thousands
        // in a value with exactly 3 trailing digits and no decimals ("1,234")
        const decimals = text.length - lastComma - 1
        normalized = decimals === 3 ? text.replace(/,/g, '') : text.replace(',', '.')
    }

    const parsed = parseFloat(normalized)
    return isNaN(parsed) ? NaN : parsed
}

//-----------------------------------------------------------------------------------------
//* \brief
//? ROUNDING POLICY
//?
//? Values are rounded exactly once, as late as possible: on the value that actually goes
//? into the XML, after duplicate lines have been summed. Rounding each row first and
//? adding afterwards inflates the declaration - 2.5 + 5.5 + 1.2 is 9, but rounded first
//? it becomes 3 + 6 + 1 = 10, and the drift grows with every line.
//?
//? So the normalizers below only put a number into a canonical decimal form (they strip
//? thousands separators and turn a decimal comma into a dot) and keep every decimal.
//? toXmlInteger() is the single place that rounds, and it is called by the XML generator.
//-----------------------------------------------------------------------------------------

//-----------------------------------------------------------------------------------------
//* \brief
//? Canonical decimal string, without the float noise of a plain toString()
//? (8 + 1.2 is 9.199999999999999 in binary floating point).
//-----------------------------------------------------------------------------------------
export const toDecimalString = (value) => {
    if (isNaN(value)) return ''
    return String(Math.round(value * 10000) / 10000)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? The one rounding step, applied to the value that goes into the XML.
//? Half-up to a whole number as [2] ch. 6.3.3 requires, and a non-zero amount that would
//? round down to 0 is reported as 1, because PositiveLongType forbids 0 and [1] states
//? that items weighing less than 1 kg are entered with the figure 1.
//? An empty value stays empty so it is reported as missing rather than as a zero.
//-----------------------------------------------------------------------------------------
export const toXmlInteger = (raw) => {
    const text = asString(raw).trim()
    if (text === '') return ''

    const parsed = parseLooseNumber(text)
    if (isNaN(parsed)) return text

    const rounded = Math.round(parsed)
    if (rounded < 1 && parsed > 0) return '1'
    return String(rounded)
}

const unchanged = (value) => ({ value, changed: false, note: '' })
const corrected = (value, note) => ({ value, changed: true, note })

//-----------------------------------------------------------------------------------------
//* \brief
//? Drops the fractional part of a code that a numeric Excel cell rendered with decimals
//? ("10059000.0" -> "10059000", "3,00" -> "3"). Only a fraction made entirely of zeros is
//? dropped, so a real decimal separator inside a code is never lost.
//-----------------------------------------------------------------------------------------
const stripZeroFraction = (text) => text.replace(/^(\d+)[.,]0+$/, '$1')

//=========================================================================================
//? NORMALIZERS - they repair what can be repaired deterministically and report what
//? they changed so the user always sees it.
//=========================================================================================

//-----------------------------------------------------------------------------------------
//* \brief
//? CN8 code. Separators are always stripped, whatever the result, so the cell never keeps
//? a value the schema cannot accept. On top of that the length is repaired whenever the
//? repair is unambiguous against the nomenclature:
//?   "3208 90 99" / "3208.90.99" -> "32089099"  (separators)
//?   "7104000"                   -> "07104000"  (leading zero eaten by a numeric cell)
//?   "8539 41"   / "853941"      -> "85394100"  (6 digit HS code, single CN8 child)
//?   "3208909900" (TARIC 10)     -> "32089099"  (first 8 digits are the CN8 code)
//? When no repair applies the digits are still returned, so the user sees
//? 'Codul NC8 "8539400" nu are exact 8 cifre' instead of the original punctuation.
//-----------------------------------------------------------------------------------------
export const normalizeCn8 = (raw) => {
    const original = asString(raw).trim()
    if (original === '') return unchanged('')

    const digits = stripZeroFraction(original).replace(/\D/g, '')
    if (digits === '') return unchanged(original)

    //? Reports the repaired code, or the bare digits when nothing could be repaired
    const settle = (value, note) => {
        if (value === original) return unchanged(value)
        return corrected(value, note)
    }

    if (digits.length === 8) {
        return settle(digits, `Cod NC8 "${original}" curățat de separatoare -> "${digits}"`)
    }
    if (digits.length === 7) {
        // A numeric Excel cell drops the single leading zero of chapters 01-09
        if (CoduriVamale.has(`0${digits}`)) {
            return settle(`0${digits}`, `Cod NC8 "${original}" completat cu zeroul din față -> "0${digits}"`)
        }
        // Otherwise a trailing digit was lost, but only if that reading is the valid one
        if (CoduriVamale.has(`${digits}0`)) {
            return settle(`${digits}0`, `Cod NC8 "${original}" completat cu zeroul final -> "${digits}0"`)
        }
    }
    // A 6 digit HS subheading, expandable only when it has a single CN8 child
    if (digits.length === 6 && CN8_PREFIX_COUNT.get(digits) === 1 && CoduriVamale.has(`${digits}00`)) {
        return settle(`${digits}00`, `Cod SA "${original}" extins la NC8 -> "${digits}00"`)
    }
    // A 10-digit TARIC code contains the CN8 code in its first 8 digits
    if (digits.length === 10 && CoduriVamale.has(digits.slice(0, 8))) {
        return settle(digits.slice(0, 8), `Cod TARIC "${original}" redus la NC8 -> "${digits.slice(0, 8)}"`)
    }
    return settle(digits, `Cod NC8 "${original}" curățat de separatoare -> "${digits}"`)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Country code. Trims, upper-cases and maps the codes an ERP commonly exports onto the
//? INS geonomenclature: EL is the Greek VAT prefix while the country code is GR, and the
//? United Kingdom is XU there (XI is Northern Ireland) - neither GB nor UK exists in it.
//-----------------------------------------------------------------------------------------
const COUNTRY_ALIASES = {
    EL: 'GR', // Greece: EL is the VAT prefix, GR the country code
    UK: 'XU', // United Kingdom excluding Northern Ireland
    GB: 'XU', // idem - Northern Ireland has its own code, XI
}

export const normalizeCountry = (raw) => {
    const original = asString(raw).trim()
    if (original === '') return unchanged('')

    let code = original.toUpperCase().replace(/[^A-Z]/g, '')
    if (COUNTRY_ALIASES[code]) code = COUNTRY_ALIASES[code]

    if (code === original) return unchanged(code)
    return corrected(code, `Țara "${original}" normalizată -> "${code}"`)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Nature of transaction. The INS nomenclature is always two levels ("4.2"), see [2]
//? ch. 6.3.7. Accepts a comma as separator and the compact "42" form; a bare single digit
//? is ambiguous and is left untouched so it gets reported as an error.
//-----------------------------------------------------------------------------------------
export const normalizeTransactionNature = (raw) => {
    const original = asString(raw).trim()
    if (original === '') return unchanged('')

    const compact = original.replace(/\s/g, '').replace(',', '.')

    if (TRANSACTION_NATURES.has(compact)) {
        return compact === original ? unchanged(compact)
            : corrected(compact, `Natura tranzacției "${original}" normalizată -> "${compact}"`)
    }
    // "42" written without the separator
    const twoDigits = compact.match(/^(\d)(\d)$/)
    if (twoDigits) {
        const dotted = `${twoDigits[1]}.${twoDigits[2]}`
        if (TRANSACTION_NATURES.has(dotted)) {
            return corrected(dotted, `Natura tranzacției "${original}" completată -> "${dotted}"`)
        }
    }
    // "4.20" / "4.2.0" produced by a numeric cell
    const trailing = compact.match(/^(\d)\.(\d)[.0]+$/)
    if (trailing) {
        const dotted = `${trailing[1]}.${trailing[2]}`
        if (TRANSACTION_NATURES.has(dotted)) {
            return corrected(dotted, `Natura tranzacției "${original}" normalizată -> "${dotted}"`)
        }
    }
    //? Nothing matched, but the separator cleanup still stands so the reported value is
    //? the one that will be checked, not the raw cell content
    if (compact !== original) {
        return corrected(compact, `Natura tranzacției "${original}" normalizată -> "${compact}"`)
    }
    return unchanged(compact)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Delivery terms (INCOTERM). Trims, upper-cases and drops any trailing place name.
//-----------------------------------------------------------------------------------------
export const normalizeDeliveryTerms = (raw) => {
    const original = asString(raw).trim()
    if (original === '') return unchanged('')

    const upper = original.toUpperCase()
    // "DAP Cluj-Napoca" -> "DAP"
    const first = upper.split(/[\s,;/-]+/)[0]
    if (DELIVERY_TERMS.has(first)) {
        return first === original ? unchanged(first)
            : corrected(first, `Condiția de livrare "${original}" normalizată -> "${first}"`)
    }
    return upper === original ? unchanged(upper)
        : corrected(upper, `Condiția de livrare "${original}" normalizată -> "${upper}"`)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Mode of transport. Single digit 1-9; strips a leading zero ("03" -> "3").
//-----------------------------------------------------------------------------------------
export const normalizeTransportMode = (raw) => {
    const original = asString(raw).trim()
    if (original === '') return unchanged('')

    const digits = stripZeroFraction(original).replace(/\D/g, '').replace(/^0+/, '')
    if (digits === original) return unchanged(digits)
    if (digits === '') return unchanged(original)
    return corrected(digits, `Modul de transport "${original}" normalizat -> "${digits}"`)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Monetary amount (invoice / statistical value). The declaration carries whole RON
//? ([1] InvoiceValue documentation, [2] ch. 6.3.5), but the rounding happens in
//? toXmlInteger() on the summed value - here the amount only gets a canonical decimal
//? form ("1.713,45" -> "1713.45") so the totals add up correctly.
//-----------------------------------------------------------------------------------------
export const normalizeAmount = (raw) => {
    const original = asString(raw).trim()
    if (original === '') return unchanged('')

    const parsed = parseLooseNumber(original)
    if (isNaN(parsed)) return unchanged(original)

    const value = toDecimalString(parsed)
    if (value === original) return unchanged(value)
    return corrected(value, `Valoarea "${original}" normalizată -> "${value}"`)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Net mass / supplementary units quantity. Same policy as normalizeAmount: decimals are
//? kept here and rounded once, at the end, on the value that reaches the XML
//? ([1] NetMass documentation, [2] ch. 6.3.3 and 6.3.4).
//-----------------------------------------------------------------------------------------
export const normalizeQuantity = (raw) => {
    const original = asString(raw).trim()
    if (original === '') return unchanged('')

    const parsed = parseLooseNumber(original)
    if (isNaN(parsed)) return unchanged(original)

    const value = toDecimalString(parsed)
    if (value === original) return unchanged(value)
    return corrected(value, `Cantitatea "${original}" normalizată -> "${value}"`)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Partner VAT (dispatches only). Strips separators and upper-cases. Never invents a
//? country prefix - a VAT without one is reported as an error instead, because guessing
//? it is what produced <PartnerCountryCode>75</PartnerCountryCode> in the broken files.
//-----------------------------------------------------------------------------------------
export const normalizePartnerVat = (raw) => {
    const original = asString(raw).trim()
    if (original === '') return unchanged('')

    const cleaned = original.toUpperCase().replace(/[\s.\-/]/g, '')
    if (cleaned === original) return unchanged(cleaned)
    return corrected(cleaned, `Codul TVA "${original}" curățat -> "${cleaned}"`)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Supplementary unit code. Only ever set from the CN8 nomenclature, so normalizing it
//? means replacing whatever the file contained with the code the nomenclature requires.
//-----------------------------------------------------------------------------------------
export const normalizeSupplUnitCode = (raw) => unchanged(asString(raw).trim())

//=========================================================================================
//? NOMENCLATURE LOOKUPS
//=========================================================================================

//-----------------------------------------------------------------------------------------
//* \brief
//? Returns the supplementary unit code the CN8 nomenclature requires for a code, or ''
//? when the code needs no supplementary unit. Returns null for an unknown CN8 code.
//-----------------------------------------------------------------------------------------
export const getRequiredSupplUnit = (cn8) => {
    const idx = CN8_INDEX.get(asString(cn8).trim())
    if (idx === undefined || idx >= RefCodes.length) return null
    return RefCodes[idx]
}

export const isKnownCn8            = (value) => CoduriVamale.has(asString(value).trim())
export const isKnownWorldCountry   = (value) => WORLD_COUNTRY_SET.has(asString(value).trim())
export const isKnownEuCountry      = (value) => EU_COUNTRY_SET.has(asString(value).trim())
export const isKnownTransportMode  = (value) => TRANSPORT_MODES.has(asString(value).trim())
export const isKnownDeliveryTerms  = (value) => DELIVERY_TERMS.has(asString(value).trim())
export const isKnownTransNature    = (value) => TRANSACTION_NATURES.has(asString(value).trim())

//-----------------------------------------------------------------------------------------
//* \brief
//? Splits a partner VAT into [countryCode, number]. Returns null when the value does not
//? start with a two-letter country prefix, instead of blindly slicing the first 2 chars.
//-----------------------------------------------------------------------------------------
export const splitPartnerVat = (raw) => {
    const value = asString(raw).trim().toUpperCase().replace(/[\s.\-/]/g, '')
    const match = value.match(/^([A-Z]{2})([0-9A-Z]+)$/)
    if (!match) return null
    return [match[1], match[2]]
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Builds the 10-digit VatNr required by VatNumberType ([0-9]{10}) from a Romanian CUI
//? written in any form ("RO40986607", "40986607", "RO 40986607"). Returns '' when the
//? value cannot produce a valid 10-digit number.
//-----------------------------------------------------------------------------------------
export const toDeclarantVatNr = (raw) => {
    const digits = asString(raw).toUpperCase().replace(/^[A-Z]+/, '').replace(/\D/g, '')
    if (digits === '' || digits.length > 10) return ''
    return digits.padStart(10, '0')
}

//=========================================================================================
//? VALIDATORS - one per field. ctx carries whatever a rule needs from the rest of the row.
//=========================================================================================

const error   = (message) => ({ severity: SEVERITY.ERROR,   message })
const warning = (message) => ({ severity: SEVERITY.WARNING, message })

//-----------------------------------------------------------------------------------------
//* \brief
//? Validates a value that PositiveLongType requires to be greater than zero
//? (xsd:long, minExclusive 0).
//?
//? Decimals are accepted here on purpose. A row holding 0.4 kg is perfectly good data:
//? it is summed with the other rows of its group and only the total is rounded, and even
//? on its own toXmlInteger() reports it as 1. Rejecting it per row would force the user
//? to round by hand, which is the drift this policy exists to avoid.
//-----------------------------------------------------------------------------------------
const validatePositiveLong = (value, label) => {
    const text = asString(value).trim()
    if (text === '') return error(`${label} lipsește`)

    const parsed = parseLooseNumber(text)
    if (isNaN(parsed)) return error(`${label} "${text}" nu este un număr`)
    if (parsed <= 0)   return error(`${label} trebuie să fie mai mare decât 0 (valoare curentă: ${text})`)
    return null
}

export const FIELD_RULES = {
    //-------------------------------------------------------------------------------------
    //? Cn8Code - xsd pattern [0-9]{8} + must exist in the CN nomenclature of the year
    //-------------------------------------------------------------------------------------
    [FIELD.COD_VAMAL]: {
        required:  true,
        normalize: normalizeCn8,
        validate:  (value) => {
            const text = asString(value).trim()
            if (text === '') return error('Codul NC8 lipsește')
            if (!/^[0-9]{8}$/.test(text)) {
                return error(`Codul NC8 "${text}" nu are exact 8 cifre`)
            }
            if (!isKnownCn8(text)) {
                return error(`Codul NC8 "${text}" nu există în Nomenclatorul Combinat`)
            }
            return null
        },
    },

    //-------------------------------------------------------------------------------------
    //? InvoiceValue - PositiveLongType, RON without decimals
    //-------------------------------------------------------------------------------------
    [FIELD.VALOARE_FISCALA]: {
        required:  true,
        normalize: normalizeAmount,
        validate:  (value) => validatePositiveLong(value, 'Valoarea fiscală'),
    },

    //-------------------------------------------------------------------------------------
    //? NetMass - PositiveLongType, whole kg
    //-------------------------------------------------------------------------------------
    [FIELD.GREUTATE_NETA]: {
        required:  true,
        normalize: normalizeQuantity,
        validate:  (value) => validatePositiveLong(value, 'Greutatea netă'),
    },

    //-------------------------------------------------------------------------------------
    //? StatisticalValue - optional in the schema, PositiveLongType when present
    //-------------------------------------------------------------------------------------
    [FIELD.VALOARE_STATISTICA]: {
        required:  false,
        normalize: normalizeAmount,
        validate:  (value, ctx = {}) => {
            if (!ctx.isValStat) return null
            return validatePositiveLong(value, 'Valoarea statistică')
        },
    },

    //-------------------------------------------------------------------------------------
    //? DeliveryTermsCode
    //-------------------------------------------------------------------------------------
    [FIELD.CONDITIE_LIVRARE]: {
        required:  true,
        normalize: normalizeDeliveryTerms,
        validate:  (value) => {
            const text = asString(value).trim()
            if (text === '') return error('Condiția de livrare lipsește')
            if (!isKnownDeliveryTerms(text)) {
                return error(`Condiția de livrare "${text}" nu există în nomenclator`)
            }
            return null
        },
    },

    //-------------------------------------------------------------------------------------
    //? NatureOfTransactionACode / ...BCode - always a two level code ("4.2")
    //-------------------------------------------------------------------------------------
    [FIELD.NATURA_TRANZACTIEI]: {
        required:  true,
        normalize: normalizeTransactionNature,
        validate:  (value) => {
            const text = asString(value).trim()
            if (text === '') return error('Natura tranzacției lipsește')
            if (!/^\d\.\d$/.test(text)) {
                return error(
                    `Natura tranzacției "${text}" nu are formatul cerut (o cifră, punct, o cifră - ex. "1.1")`
                )
            }
            if (!isKnownTransNature(text)) {
                return error(`Natura tranzacției "${text}" nu există în nomenclator`)
            }
            return null
        },
    },

    //-------------------------------------------------------------------------------------
    //? ModeOfTransportCode
    //-------------------------------------------------------------------------------------
    [FIELD.MOD_TRANSPORT]: {
        required:  true,
        normalize: normalizeTransportMode,
        validate:  (value) => {
            const text = asString(value).trim()
            if (text === '') return error('Modul de transport lipsește')
            if (!isKnownTransportMode(text)) {
                return error(`Modul de transport "${text}" nu există în nomenclator (valori acceptate: 1-9)`)
            }
            return null
        },
    },

    //-------------------------------------------------------------------------------------
    //? CountryOfOrigin - any country of the world ([2] ch. 6.3.2)
    //-------------------------------------------------------------------------------------
    [FIELD.TARA_ORIGINE]: {
        required:  true,
        normalize: normalizeCountry,
        validate:  (value) => {
            const text = asString(value).trim()
            if (text === '') return error('Țara de origine lipsește')
            if (text.length !== 2) {
                return error(`Țara de origine "${text}" nu are 2 litere`)
            }
            if (!isKnownWorldCountry(text)) {
                return error(`Țara de origine "${text}" nu există în nomenclatorul de țări`)
            }
            if (EXCLUDED_TERRITORIES.has(text)) {
                return warning(
                    `Țara de origine "${text}" este un teritoriu exclus din sistemul INTRASTAT ` +
                    `(se declară vamal, nu prin INTRASTAT)`
                )
            }
            return null
        },
    },

    //-------------------------------------------------------------------------------------
    //? CountryOfConsignment (arrivals) / CountryOfDestination (dispatches).
    //? Must be an EU member state and can never be the reporting country ([2] ch. 6.3.2).
    //-------------------------------------------------------------------------------------
    [FIELD.TARA_EXPEDIERE]: {
        required:  true,
        normalize: normalizeCountry,
        validate:  (value, ctx = {}) => {
            const text = asString(value).trim()
            const isArrival = ctx.type === 'achizitii'
            const label = isArrival ? 'Țara de expediere' : 'Țara de destinație'

            if (text === '') return error(`${label} lipsește`)
            if (text.length !== 2) return error(`${label} "${text}" nu are 2 litere`)
            if (text === REPORTING_COUNTRY) {
                return error(
                    `${label} nu poate fi "${REPORTING_COUNTRY}" - trebuie să fie un alt stat membru UE`
                )
            }
            if (!isKnownEuCountry(text)) {
                return error(`${label} "${text}" nu este un stat membru UE`)
            }
            return null
        },
    },

    //-------------------------------------------------------------------------------------
    //? SupplUnitCode - dictated by the CN8 nomenclature, never free text
    //-------------------------------------------------------------------------------------
    [FIELD.COD_UMS]: {
        required:  false,
        normalize: normalizeSupplUnitCode,
        validate:  (value, ctx = {}) => {
            const required = getRequiredSupplUnit(ctx.cn8)
            // The CN8 code itself is invalid - that error is already reported on its column
            if (required === null) return null

            const text = asString(value).trim()
            if (required === '') {
                if (text !== '') {
                    return error(
                        `Codul NC8 "${ctx.cn8}" nu are unitate de măsură suplimentară, dar coloana conține "${text}"`
                    )
                }
                return null
            }
            if (text === '') {
                return error(`Codul NC8 "${ctx.cn8}" cere unitatea de măsură suplimentară "${required}"`)
            }
            if (text !== required) {
                return error(
                    `Unitatea de măsură suplimentară "${text}" nu corespunde codului NC8 "${ctx.cn8}" (corect: "${required}")`
                )
            }
            return null
        },
    },

    //-------------------------------------------------------------------------------------
    //? QtyInSupplUnits - PositiveLongType when the CN8 code requires a supplementary unit
    //-------------------------------------------------------------------------------------
    [FIELD.VALOARE_UMS]: {
        required:  false,
        normalize: normalizeQuantity,
        validate:  (value, ctx = {}) => {
            const required = getRequiredSupplUnit(ctx.cn8)
            if (required === null) return null

            const text = asString(value).trim()
            if (required === '') {
                if (text !== '' && parseLooseNumber(text) > 0) {
                    return error(
                        `Codul NC8 "${ctx.cn8}" nu are unitate de măsură suplimentară, dar cantitatea este "${text}"`
                    )
                }
                return null
            }
            return validatePositiveLong(text, `Cantitatea în "${required}"`)
        },
    },

    //-------------------------------------------------------------------------------------
    //? PartnerVatNr + PartnerCountryCode (dispatches only) - [2] ch. 6.3.1
    //-------------------------------------------------------------------------------------
    [FIELD.VAT]: {
        required:  true,
        normalize: normalizePartnerVat,
        validate:  (value, ctx = {}) => {
            if (ctx.type === 'achizitii') return null

            const text = asString(value).trim()
            if (text === '') return error('Codul TVA al partenerului lipsește')
            if (text === UNKNOWN_PARTNER_VAT) return null

            const parts = splitPartnerVat(text)
            if (!parts) {
                return error(
                    `Codul TVA "${text}" nu începe cu prefixul de țară (ex. "IT67120014"). ` +
                    `Dacă partenerul nu poate fi identificat, folosiți "${UNKNOWN_PARTNER_VAT}"`
                )
            }
            const [country, number] = parts
            if (!isKnownEuCountry(country)) {
                return error(`Prefixul de țară "${country}" din codul TVA "${text}" nu este un stat membru UE`)
            }
            if (country === REPORTING_COUNTRY) {
                return error(`Codul TVA al partenerului nu poate fi românesc ("${text}")`)
            }
            if (number.length < 2) {
                return error(`Codul TVA "${text}" este prea scurt`)
            }
            if (ctx.knownInvalidVats && ctx.knownInvalidVats.has(text)) {
                return error(`Codul TVA "${text}" a fost respins de verificarea VIES`)
            }
            return null
        },
    },
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Runs the normalizer of a field. Fields without a rule are returned untouched.
//-----------------------------------------------------------------------------------------
export const normalizeField = (fieldName, value) => {
    const rule = FIELD_RULES[fieldName]
    if (!rule || !rule.normalize) return unchanged(asString(value))
    return rule.normalize(value)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Runs the validator of a field. Returns null when the field is valid or has no rule.
//-----------------------------------------------------------------------------------------
export const validateField = (fieldName, value, ctx = {}) => {
    const rule = FIELD_RULES[fieldName]
    if (!rule || !rule.validate) return null
    return rule.validate(value, ctx)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Validates a whole flat item array (the shape passed to the XML generator).
//? Returns an array of { field, label, severity, message }.
//-----------------------------------------------------------------------------------------
export const validateItemArray = (item, ctx = {}) => {
    const findings = []
    const cn8 = asString(item[ITEM.CN8]).trim()
    const itemCtx = { ...ctx, cn8 }

    const checks = [
        [FIELD.COD_VAMAL,          item[ITEM.CN8]],
        [FIELD.VALOARE_FISCALA,    item[ITEM.INVOICE_VALUE]],
        [FIELD.GREUTATE_NETA,      item[ITEM.NET_MASS]],
        [FIELD.CONDITIE_LIVRARE,   item[ITEM.DELIVERY_TERMS]],
        [FIELD.TARA_ORIGINE,       item[ITEM.COUNTRY_ORIGIN]],
        [FIELD.TARA_EXPEDIERE,     item[ITEM.COUNTRY_PARTNER]],
        [FIELD.VALOARE_STATISTICA, item[ITEM.STAT_VALUE]],
        [FIELD.NATURA_TRANZACTIEI, item[ITEM.TRANSACTION_NATURE]],
        [FIELD.MOD_TRANSPORT,      item[ITEM.TRANSPORT_MODE]],
        [FIELD.COD_UMS,            item[ITEM.SUPPL_UNIT_CODE]],
        [FIELD.VALOARE_UMS,        item[ITEM.SUPPL_UNIT_QTY]],
    ]
    if (ctx.type !== 'achizitii') {
        checks.push([FIELD.VAT, item[ITEM.PARTNER_VAT]])
    }

    checks.forEach(([field, value]) => {
        const finding = validateField(field, value, itemCtx)
        if (finding) {
            findings.push({ field, label: FIELD_LABELS[field], ...finding })
        }
    })
    return findings
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Validates the declaration header data before it reaches the XML generator.
//-----------------------------------------------------------------------------------------
export const validateHeaderData = (data) => {
    const findings = []
    const push = (label, finding) => finding && findings.push({ label, ...finding })

    if (!/^[0-9]{10}$/.test(asString(data.compVat))) {
        push('CUI declarant', error(
            `CUI-ul "${data.compVat}" nu poate fi transformat în cele 10 cifre cerute de schemă`
        ))
    }
    if (data.tert && !/^[0-9]{10}$/.test(asString(data.tertVat))) {
        push('CUI declarant terț', error(
            `CUI-ul terțului "${data.tertVat}" nu poate fi transformat în cele 10 cifre cerute de schemă`
        ))
    }
    if (!/^\d{4}-\d{2}$/.test(asString(data.refPeriod))) {
        push('Perioadă de referință', error(`Perioada "${data.refPeriod}" nu are formatul AAAA-LL`))
    }
    if (asString(data.compName).trim() === '') {
        push('Denumire firmă', error('Denumirea firmei lipsește'))
    }
    if (asString(data.lName).trim() === '') {
        push('Persoană de contact', error('Numele persoanei de contact lipsește'))
    }
    if (asString(data.phone).trim() === '') {
        push('Telefon', error('Numărul de telefon lipsește'))
    }
    if (!Array.isArray(data.itemArray) || data.itemArray.length < 1) {
        push('Linii declarație', error('Declarația nu conține nicio linie'))
    }
    return findings
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Convenience: does a findings list contain anything that blocks generation?
//-----------------------------------------------------------------------------------------
export const hasBlockingFindings = (findings) =>
    findings.some(f => f.severity === SEVERITY.ERROR)
